// src/router/AppRouter.tsx

import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import PageLoader from '../components/common/PageLoader';
import RequireAuth from '../components/auth/RequireAuth';
// Inbound calling (ADDITIVE, Phase IV) — the single global Twilio Device owner
// and the globally-mounted incoming-call UI.
import TelephonyProvider from '../telephony/TelephonyProvider';
import GlobalIncomingCallUI from '../telephony/GlobalIncomingCallUI';

// =============================================================================
// Auth Pages (public)
// =============================================================================
import LoginPage from '../pages/LoginPage';
import OAuthCallbackPage from '../pages/OAuthCallbackPage';

// =============================================================================
// Core / Everyday Pages (eagerly loaded for instant navigation)
// =============================================================================
import HomePage from '../pages/HomePage';
import WorkspacesPage from '../pages/WorkspacesPage';
import ConversationsPage from '../pages/ConversationsPage';
import ConversationDetailPage from '../pages/ConversationDetailPage';
import ContactsHubPage from '../pages/ContactsHubPage';
import TasksPage from '../pages/TasksPage';
import RecurringTasksPage from '../pages/RecurringTasksPage';
import PipelinePage from '../pages/PipelinePage';
import SchedulePage from '../pages/SchedulePage';
import CirclesPage from '../pages/CirclesPage';
import CompliancePage from '../pages/CompliancePage';
import CircleDetailPage from '../pages/CircleDetailPage';
import ReferralsPage from '../pages/ReferralsPage';
import TeamPage from '../pages/TeamPage';
import FilesPage from '../pages/FilesPage';
import GoalsPage from '../pages/GoalsPage';
import WeeklyPlanPage from '../pages/WeeklyPlanPage';
import EmailsPage from '../pages/EmailsPage';
import RequireMenuAccess from './RequireMenuAccess';
import LegacyRouteRedirect, { LegacyCompanyDetailRedirect } from './LegacyRouteRedirect';

// =============================================================================
// Onboarding (lazy-loaded - used only for new users)
// =============================================================================
const OnboardingWizardPage = React.lazy(() =>
  import('../pages/onboarding/OnboardingWizardPage')
);

// =============================================================================
// Heavy / Admin / Power-User Pages (lazy-loaded for smaller initial bundle)
// =============================================================================
const AutomationsPage = React.lazy(() =>
  import('../pages/AutomationsPage').then((m) => ({ default: m.AutomationsPage }))
);
const AIMemoryPage = React.lazy(() =>
  import('../pages/AIMemoryPage').then((m) => ({ default: m.AIMemoryPage }))
);
const AuditLogPage = React.lazy(() => import('../pages/AuditLogPage'));
const SystemHealthPage = React.lazy(() => import('../pages/SystemHealthPage'));
const LaunchCheckPage = React.lazy(() => import('../pages/LaunchCheckPage'));
const CentralDiagnosticsPage = React.lazy(() => import('../pages/CentralDiagnosticsPage'));
const ChannelsPage = React.lazy(() => import('../pages/ChannelsPage'));
const MarketplacePage = React.lazy(() => import('../pages/MarketplacePage'));
const RecruitmentPage = React.lazy(() => import('../pages/RecruitmentPage'));
const BusinessProfilePage = React.lazy(() => import('../pages/BusinessProfilePage'));
const PortfolioPage = React.lazy(() => import('../pages/PortfolioPage'));
const SettingsPage = React.lazy(() => import('../pages/SettingsPage'));
const IntegrationsPage = React.lazy(() => import('../pages/IntegrationsPage'));
// PR-44 Phase 7 — Providers page (settings › providers). Read-only
// dashboard + safe editing for Outbound-mirrored providers.
const ProvidersPage = React.lazy(() => import('../pages/ProvidersPage'));
const MenuTemplatesPage = React.lazy(() => import('../pages/MenuTemplatesPage'));
const MessagesPage = React.lazy(() => import('../pages/MessagesPage'));
const FormsPage = React.lazy(() => import('../pages/FormsPage'));
const ProjectsPage = React.lazy(() => import('../pages/ProjectsPage'));
const OfferingsPage = React.lazy(() => import('../pages/OfferingsPage'));
const PaymentsPage = React.lazy(() => import('../pages/PaymentsPage'));
const CallLogPage = React.lazy(() => import('../pages/CallLogPage'));
const VoicemailsPage = React.lazy(() => import('../pages/VoicemailsPage'));
const PhonePage = React.lazy(() => import('../pages/PhonePage'));
const ContactWorkspacePage = React.lazy(() => import('../pages/ContactWorkspacePage'));
// SMS is handled inside EmailsPage (Unibox) — no separate page needed
// const SmsPage = React.lazy(() => import('../pages/SmsPage'));
const VoiceAgentPage = React.lazy(() => import('../pages/VoiceAgentPage'));
// CompaniesPage is now lazy-loaded by ContactsHubPage, which owns the tab it
// renders in — see that file. It is no longer mounted directly by a route.
const CompanyDetailPage = React.lazy(() => import('../pages/CompanyDetailPage'));
const OffloadResponsibilitiesPage = React.lazy(() => import('../pages/OffloadResponsibilitiesPage'));

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* =============================================================== */}
        {/* Public Routes - No Authentication Required                      */}
        {/* =============================================================== */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />

        {/* Onboarding - Outside AppLayout (has its own layout) */}
        <Route
          path="/onboarding"
          element={
            <RequireAuth>
              <Suspense fallback={<PageLoader />}>
                <OnboardingWizardPage />
              </Suspense>
            </RequireAuth>
          }
        />

        {/* =============================================================== */}
        {/* Protected Routes - Authentication Required                      */}
        {/* =============================================================== */}
        {/* TelephonyProvider (ADDITIVE — inbound calling, Phase IV).
            Mounted INSIDE RequireAuth and OUTSIDE the route outlet, so:
              - it only ever exists for an authenticated user;
              - it owns exactly ONE Twilio Device for this user/workspace/tab;
              - it survives navigation between CRM routes (it is not remounted
                when the matched child route changes), so a call in progress is
                never dropped by a route change.
            Every existing consumer of useTwilioVoiceDevice() now transparently
            shares this one Device instead of constructing its own. */}
        <Route
          element={
            <RequireAuth>
              <TelephonyProvider>
                <AppLayout />
                <GlobalIncomingCallUI />
              </TelephonyProvider>
            </RequireAuth>
          }
        >
          {/* =============================================================== */}
          {/* Core Routes - Eagerly Loaded                                    */}
          {/* =============================================================== */}
          <Route path="/" element={<HomePage />} />
          <Route path="/workspaces" element={<WorkspacesPage />} />
          {/*
            AI assistant conversations. Deliberately NOT relabelled as
            messaging: these are `ConversationSession`/`ConversationMessage`
            rows whose sender types include `assistant` and whose actions
            create tasks and send email. Keeping the route stable preserves
            existing deep links; it simply no longer answers to "Messages".
          */}
          <Route path="/conversations" element={<ConversationsPage />} />
          <Route path="/conversations/:sessionId" element={<ConversationDetailPage />} />

          {/*
            M1 — `/messages` is now the canonical Contact communications
            inbox (the Unibox), which is what "Messages" means in the product.
            The same component still serves `/emails` via redirect below, so
            this is a rename of the entry point, not a second implementation.
          */}
          <Route
            path="/messages"
            element={
              <RequireMenuAccess menuKey="messages"><EmailsPage /></RequireMenuAccess>
            }
          />
          {/*
            Backward compatibility: `/emails` was the only way to reach the
            Unibox, so bookmarks and stored deep links point at it. The
            redirect preserves the query string, which carries threadId /
            messageId / medium / unreadOnly / status.
          */}
          <Route path="/emails" element={<LegacyRouteRedirect to="/messages" />} />
          {/*
            ONE Contacts destination: People and Companies are tabs of it, and
            every surface in the area shares the `/contacts` prefix so the
            suite shell's prefix-matched rail lights exactly one entry. See
            `ContactsHubPage` for why the tab is a path, not a query parameter.
          */}
          <Route
            path="/contacts"
            element={
              <RequireMenuAccess menuKey="contacts"><ContactsHubPage tab="people" /></RequireMenuAccess>
            }
          />
          <Route
            path="/contacts/companies"
            element={
              <RequireMenuAccess menuKey="contacts"><ContactsHubPage tab="companies" /></RequireMenuAccess>
            }
          />
          <Route
            path="/tasks"
            element={
              <RequireMenuAccess menuKey="tasks"><TasksPage /></RequireMenuAccess>
            }
          />
          <Route
            path="/recurring-tasks"
            element={
              <RequireMenuAccess menuKey="tasks"><RecurringTasksPage /></RequireMenuAccess>
            }
          />
          <Route
            path="/pipeline"
            element={
              <RequireMenuAccess menuKey="pipeline"><PipelinePage /></RequireMenuAccess>
            }
          />
          {/* Phase C: Meetups consolidated into Schedule. Preserve old links —
              redirect to Schedule pre-filtered to Meetups (query string wins). */}
          <Route path="/meetups" element={<LegacyRouteRedirect to="/schedule" defaultSearch="kind=meetup" />} />
          <Route
            path="/schedule"
            element={
              <RequireMenuAccess menuKey="schedule"><SchedulePage /></RequireMenuAccess>
            }
          />
          <Route
            path="/circles"
            element={
              <RequireMenuAccess menuKey="circles"><CirclesPage /></RequireMenuAccess>
            }
          />
          <Route
            path="/circles/:id"
            element={
              <RequireMenuAccess menuKey="circles"><CircleDetailPage /></RequireMenuAccess>
            }
          />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route
            path="/referrals"
            element={
              <RequireMenuAccess menuKey="referrals"><ReferralsPage /></RequireMenuAccess>
            }
          />
          <Route
            path="/team"
            element={
              <RequireMenuAccess menuKey="team"><TeamPage /></RequireMenuAccess>
            }
          />
          <Route
            path="/files"
            element={
              <RequireMenuAccess menuKey="files"><FilesPage /></RequireMenuAccess>
            }
          />
          <Route path="/goals" element={<GoalsPage />} />
          <Route path="/plan" element={<WeeklyPlanPage />} />

          {/* =============================================================== */}
          {/* Lazy-Loaded Routes - Heavy / Admin / Power-User                 */}
          {/* =============================================================== */}
          {/*
            M1 — WeOpen live sessions get their own honest name.

            This page renders WeOpen live chat sessions, which is a distinct
            bounded context from Contact communications. It previously sat on
            `/messages` and was the only thing the command palette's
            "Go to Messages" could reach, which is a large part of why Messages
            appeared not to work. `/messages` now belongs to the Unibox (see
            the eager route above); this surface keeps its functionality under
            a name that says what it is.
          */}
          <Route
            path="/live-sessions"
            element={
              <Suspense fallback={<PageLoader />}>
                <MessagesPage />
              </Suspense>
            }
          />
          <Route
            path="/forms"
            element={
              <RequireMenuAccess menuKey="forms">
              <Suspense fallback={<PageLoader />}>
                <FormsPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/projects"
            element={
              <RequireMenuAccess menuKey="projects">
              <Suspense fallback={<PageLoader />}>
                <ProjectsPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/offerings"
            element={
              <RequireMenuAccess menuKey="offerings">
              <Suspense fallback={<PageLoader />}>
                <OfferingsPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/payments"
            element={
              <RequireMenuAccess menuKey="payments">
              <Suspense fallback={<PageLoader />}>
                <PaymentsPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          {/*
            Company detail, now under the Contacts prefix. The page component
            is unchanged — only where it is mounted.
          */}
          <Route
            path="/contacts/companies/:id"
            element={
              <RequireMenuAccess menuKey="contacts">
              <Suspense fallback={<PageLoader />}>
                <CompanyDetailPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          {/*
            Backward compatibility. `/companies` and `/companies/:id` were the
            only way in for the whole life of the feature, so bookmarks, stored
            links and anything a member has shared point at them. They redirect
            rather than 404, and both carry the query string across.
          */}
          <Route path="/companies" element={<LegacyRouteRedirect to="/contacts/companies" />} />
          <Route path="/companies/:id" element={<LegacyCompanyDetailRedirect />} />
          <Route
            path="/business-profile"
            element={
              <RequireMenuAccess menuKey="businessProfile">
              <Suspense fallback={<PageLoader />}>
                <BusinessProfilePage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/portfolio"
            element={
              <RequireMenuAccess menuKey="portfolio">
              <Suspense fallback={<PageLoader />}>
                <PortfolioPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/recruitment"
            element={
              <RequireMenuAccess menuKey="recruitment">
              <Suspense fallback={<PageLoader />}>
                <RecruitmentPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/marketplace"
            element={
              <RequireMenuAccess menuKey="marketplace">
              <Suspense fallback={<PageLoader />}>
                <MarketplacePage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/channels"
            element={
              <RequireMenuAccess menuKey="channels">
              <Suspense fallback={<PageLoader />}>
                <ChannelsPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/phone"
            element={
              <RequireMenuAccess menuKey="phone">
              <Suspense fallback={<PageLoader />}>
                <PhonePage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/phone/contact/:crmContactId"
            element={
              <Suspense fallback={<PageLoader />}>
                <ContactWorkspacePage />
              </Suspense>
            }
          />
          {/*
            SMS has never been a standalone surface — it is a medium filter on
            the Contact communications inbox. Now points at the canonical
            `/messages` path rather than hopping through the `/emails`
            redirect. An explicit incoming query string still wins.
          */}
          <Route
            path="/sms"
            element={<LegacyRouteRedirect to="/messages" defaultSearch="medium=sms" />}
          />
          <Route
            path="/call-log"
            element={
              <RequireMenuAccess menuKey="phone">
              <Suspense fallback={<PageLoader />}>
                <CallLogPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/voicemails"
            element={
              <RequireMenuAccess menuKey="phone">
              <Suspense fallback={<PageLoader />}>
                <VoicemailsPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/voice-agent"
            element={
              <Suspense fallback={<PageLoader />}>
                <VoiceAgentPage />
              </Suspense>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireMenuAccess menuKey="settings">
              <Suspense fallback={<PageLoader />}>
                <SettingsPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/integrations"
            element={
              <RequireMenuAccess menuKey="settings">
              <Suspense fallback={<PageLoader />}>
                <IntegrationsPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/settings/providers"
            element={
              <RequireMenuAccess menuKey="settings">
              <Suspense fallback={<PageLoader />}>
                <ProvidersPage />
              </Suspense>
              </RequireMenuAccess>
            }
          />
          <Route
            path="/menu-templates"
            element={
              <Suspense fallback={<PageLoader />}>
                <MenuTemplatesPage />
              </Suspense>
            }
          />

          {/* AI & Automations - Lazy loaded */}
          <Route
            path="/ai-memory"
            element={
              <Suspense fallback={<PageLoader />}>
                <AIMemoryPage />
              </Suspense>
            }
          />
          <Route
            path="/automations"
            element={
              <Suspense fallback={<PageLoader />}>
                <AutomationsPage />
              </Suspense>
            }
          />
          <Route
            path="/audit"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuditLogPage />
              </Suspense>
            }
          />
          <Route
            path="/system/health"
            element={
              <Suspense fallback={<PageLoader />}>
                <SystemHealthPage />
              </Suspense>
            }
          />
          <Route
            path="/system/launch-check"
            element={
              <Suspense fallback={<PageLoader />}>
                <LaunchCheckPage />
              </Suspense>
            }
          />
          <Route
            path="/system/central-diagnostics"
            element={
              <Suspense fallback={<PageLoader />}>
                <CentralDiagnosticsPage />
              </Suspense>
            }
          />

          {/* Offload Responsibilities (WeOpen placeholder) */}
          <Route
            path="/offload-responsibilities"
            element={
              <Suspense fallback={<PageLoader />}>
                <OffloadResponsibilitiesPage />
              </Suspense>
            }
          />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

function NotFoundPage() {
  return (
    <div className="text-center py-12">
      <h1 className="text-4xl font-bold text-gray-900 mb-2">404</h1>
      <p className="text-gray-500">Page not found.</p>
    </div>
  );
}
